import React, { FC } from 'react';
import { useUrlParams } from '@aleph/shared-components';
import { useQuery } from '@apollo/client';
import {
	getDashboardChartStatsQuery,
} from 'wb-reddit/app/routes/superGrid/cellRenderers/name/api/chartStats';
import { meanBy, sumBy } from 'lodash';
import { Placeholder } from 'rsuite';
import { divideCurrencyFields } from 'wb-reddit/app/routes/superGrid/common/constants';
import classNames from 'classnames';
import RedditDashboardColumnChart from 'wb-reddit/app/routes/superGrid/cellRenderers/name/charts/column';
import RedditDashboardPieChart from 'wb-reddit/app/routes/superGrid/cellRenderers/name/charts/pie';
import RedditDashboardPieChartLoader from 'wb-reddit/app/routes/superGrid/cellRenderers/name/charts/pieLoader';
import { ICellRendererParams } from 'ag-grid-community';
import { useNavigate, useParams } from 'react-router-dom';

export const CALCULATED_FIELDS = ['ctr', 'ecpm', 'cpc'];

const NameDashboardCharts: FC<ICellRendererParams> = (params) => {
	const navigate = useNavigate();

	const { params: urlParams } = useUrlParams({ navigate, useParams });

	const selectedBreakdown = params?.data?.clientBreakdown ?? null;
	const selectedKpi = selectedBreakdown === 'campaignObjective' ? 'spend' : (params?.data?.clientKpi ?? null);
	const isKpiCalculated = CALCULATED_FIELDS.includes(selectedKpi);

	const pinnedColsWidth = params.columnApi?.getDisplayedLeftColumns()
		?.reduce((accu, col) => accu + col.getActualWidth(), 0);

	const { data, loading } = useQuery(getDashboardChartStatsQuery(selectedBreakdown, selectedKpi), {
		variables: {
			filters: {
				...urlParams.filters,
				clientId: [{ value: [params?.data?.clientId] }],
				date:     { value: urlParams.date },
			},
			breakdown: selectedBreakdown,
			kpi:       selectedKpi,
		},
		skip: !selectedBreakdown,
	});

	const limitedData =	(
		data?.stats?.items?.length > 6 ? [
			...data?.stats?.items?.slice(0, 5) ?? [],
			{
				breakdown: `Others ${isKpiCalculated ? 'avg' : 'sum'}`,
				kpi:       (isKpiCalculated ? meanBy : sumBy)(data?.stats?.items?.slice(5), 'kpi'),
			},
		] : (data?.stats?.items ?? [])
	).map(stat => ({
		...stat,
		kpi:      parseFloat(((stat?.kpi ?? 0) / (divideCurrencyFields[selectedKpi] ?? 1)).toFixed(2)),
		currency: params.data?.currency,
	}))?.filter(stat => (stat?.kpi ?? 0) > 0);

	if (!loading && !limitedData?.length) {
		return (
			<div className="mt-[86px] mx-[12px]">
				<div className="h-[1px] bg-[#ccd6eb]" />
			</div>
		);
	}

	return (
		<div
			className="h-full p-[12px] gap-[24px] flex overflow-hidden"
		>
			<div
				className={classNames('basis-2/3 grow-0 h-full flex items-center justify-center', {
					'!basis-full': isKpiCalculated,
				})}
			>
				{
					loading ? <Placeholder.Graph
						active
						className="!w-full !h-full rounded-[8px]"
					/> : <RedditDashboardColumnChart
						key={pinnedColsWidth}
						chartData={limitedData}
						isCurrency={!!divideCurrencyFields[selectedKpi]}
					/>
				}
			</div>

			{
				!isKpiCalculated
					&& <div className="basis-1/3 grow-0 h-full flex items-center overflow-hidden">
						{
							loading ? <RedditDashboardPieChartLoader /> : <RedditDashboardPieChart
								key={pinnedColsWidth}
								chartData={limitedData}
							/>
						}
					</div>
			}
		</div>
	);
};

export default NameDashboardCharts;
